import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  SearchBox,
  PageHeading,
  Loader,
  NoData,
  Toastify,
} from "atoms";
import Table from "molecules/Table";
import MemoDeleteIcon from "assets/icons/DeleteIcon";
import MemoEdit from "assets/icons/Edit";
import MemoAdd from "assets/icons/Add";
import { AddNewModal } from "./AddNewModal";
import { ConfirmationModal } from "molecules/Modals";
import { usePropsoch } from "contexts/Propsoch";

const MasterTemplateTable = ({
  actions: { get, create, update, remove } = {},
  pageHeading,
  addRecord,
  searchRecord,
  table: {
    tableHeader,
    tableRowDatas,
    actions: { editable, removable },
    ...tableProps
  } = { actions: {} },
  actionModal: { title: actionModalTitle, isLocation, CustomModal } = {},
}) => {
  const {
    state: { userRole },
  } = usePropsoch();

  const [search, setSearch] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);

  const [loading, setLoading] = useState(false);

  const [records, setRecords] = useState([]);

  const fetch = useCallback(() => {
    get && setLoading(true);
    get?.()
      .then((data) => {
        setRecords(data?.data);
      })
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  }, [get]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const [deleteModal, setDeleteModal] = useState(false);

  const isAuthorisedToAdd = useMemo(() => {
    return userRole?.isSuperadmin || userRole?.isAdmin;
  }, [userRole?.isSuperadmin, userRole?.isAdmin]);

  const isAuthorisedToEdit = useMemo(() => {
    return userRole?.isSuperadmin || userRole?.isAdmin;
  }, [userRole?.isSuperadmin, userRole?.isAdmin]);

  const isAuthorisedToDelete = useMemo(() => {
    return userRole?.isSuperadmin || userRole?.isAdmin;
  }, [userRole?.isSuperadmin, userRole?.isAdmin]);

  return (
    <Box>
      <Flex justifyContent="space-between" pb="2.4rem">
        <PageHeading text={pageHeading || ""} mt="1.8rem" />
        {addRecord && (
          <Button
            variant="primary"
            width="fit-content"
            px="2rem"
            height="4.8rem"
            onClick={() => setModalVisible({ visible: true })}
            disabled={!isAuthorisedToAdd}
          >
            <Flex justifyContent="center" alignItems="center" gridGap="2rem">
              <MemoAdd />
              {addRecord}
            </Flex>
          </Button>
        )}
      </Flex>
      <Card>
        {searchRecord && (
          <SearchBox
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClose={() => setSearch("")}
            placeholder={searchRecord}
            style={{ width: "30rem" }}
            removeValidationIcon={!search}
          />
        )}
        <Box mt="2.4rem" {...(loading && { minHeight: "4rem" })}>
          <Loader loading={loading}>
            <Table
              tableHeader={tableHeader}
              borderColor="neutral.500"
              {...tableProps}
            >
              <Box as="tbody" bg="white">
                {records
                  ?.filter((record) =>
                    searchRecord
                      ? record?.name
                          ?.toLowerCase()
                          ?.includes(search?.toLowerCase())
                      : true
                  )
                  ?.map((record, index) => (
                    <tr key={index}>
                      {tableRowDatas?.map((rowData) => rowData.render(record))}
                      <td>
                        <Grid
                          gridAutoFlow="column"
                          gridGap="1.8rem"
                          justifyContent="start"
                          alignItems="center"
                          color="neutral.600"
                        >
                          {editable && (
                            <Box
                              color={
                                isAuthorisedToEdit
                                  ? "neutral.900"
                                  : "neutral.300"
                              }
                              cursor={
                                isAuthorisedToEdit ? "pointer" : "not-allowed"
                              }
                              onClick={
                                isAuthorisedToEdit
                                  ? () =>
                                      setModalVisible({ visible: true, record })
                                  : undefined
                              }
                            >
                              <MemoEdit height="1.8rem" width="1.8rem" />
                            </Box>
                          )}
                          {removable && (
                            <Box
                              color={
                                isAuthorisedToDelete
                                  ? "accents.100"
                                  : "neutral.300"
                              }
                              cursor={
                                isAuthorisedToDelete ? "pointer" : "not-allowed"
                              }
                              onClick={
                                isAuthorisedToDelete
                                  ? () => {
                                      setDeleteModal({
                                        onClose: () => setDeleteModal(false),
                                        icon: {
                                          Icon: MemoDeleteIcon,
                                          ICprops: { color: "accents.100" },
                                        },
                                        textOne: "Are you sure you want to",
                                        textTwo: "delete",
                                        textThree: `the ${pageHeading} `,
                                        textFour:
                                          tableRowDatas?.[0]?.render(record),
                                        onConfirm: () => {
                                          setLoading(true);
                                          remove(record?.id)
                                            ?.then(() => {
                                              Toastify(
                                                "success",
                                                `${record?.name} deleted successfully`
                                              );
                                            })
                                            ?.catch((e) => {
                                              console.error(e);
                                              Toastify(
                                                "error",
                                                `Failed to delete ${record?.name}`
                                              );
                                            })
                                            .finally(() => {
                                              fetch();
                                            });
                                          setDeleteModal(false);
                                        },
                                      });
                                    }
                                  : undefined
                              }
                            >
                              <MemoDeleteIcon height="2rem" width="2rem" />
                            </Box>
                          )}
                        </Grid>
                      </td>
                    </tr>
                  ))}
              </Box>
            </Table>
            {(!records ||
              records?.filter((record) =>
                searchRecord
                  ? record?.name?.toLowerCase()?.includes(search?.toLowerCase())
                  : true
              )?.length === 0) && <NoData />}
          </Loader>
        </Box>
      </Card>
      {isModalVisible?.visible &&
        (CustomModal ? (
          <CustomModal
            isOpen={isModalVisible?.visible}
            onClose={() => setModalVisible(false)}
            title={actionModalTitle}
            isLocation={isLocation}
            loading={isModalVisible?.loading}
            error={isModalVisible?.error}
            resetError={() =>
              setModalVisible((prev) => ({
                ...prev,
                error: "",
              }))
            }
            preRecord={isModalVisible?.record}
            onSubmit={(values) => {
              setModalVisible((prev) => ({ ...prev, loading: true }));
              if (isModalVisible?.record) {
                (isLocation
                  ? update(isModalVisible?.record?.id, values)
                  : update(isModalVisible?.record?.id, values)
                )
                  ?.then(() => {
                    setModalVisible(false);
                    Toastify("success", `${values?.name} updated successfully`);
                  })
                  ?.catch((e) => {
                    setModalVisible((prev) => ({
                      ...prev,
                      loading: false,
                      error: e?.message,
                    }));
                    Toastify("error", `Failed to update ${values?.name}`);
                  })
                  .finally(() => {
                    fetch();
                  });
              } else {
                (isLocation ? create(values) : create(values))
                  ?.then(() => {
                    setModalVisible(false);
                    Toastify("success", `${values?.name} created successfully`);
                  })
                  ?.catch((e) => {
                    setModalVisible((prev) => ({
                      ...prev,
                      loading: false,
                      error: e?.message,
                    }));
                    Toastify("error", `Failed to create ${values?.name}`);
                  })
                  .finally(() => {
                    fetch();
                  });
              }
            }}
          />
        ) : (
          <AddNewModal
            isOpen={isModalVisible?.visible}
            onClose={() => setModalVisible(false)}
            title={actionModalTitle}
            isLocation={isLocation}
            loading={isModalVisible?.loading}
            error={isModalVisible?.error}
            resetError={() =>
              setModalVisible((prev) => ({
                ...prev,
                error: "",
              }))
            }
            preRecord={isModalVisible?.record}
            onSubmit={({ name, locationId }) => {
              setModalVisible((prev) => ({ ...prev, loading: true }));
              if (isModalVisible?.record) {
                (isLocation
                  ? update(isModalVisible?.record?.id, name, locationId)
                  : update(isModalVisible?.record?.id, name)
                )
                  ?.then(() => {
                    setModalVisible(false);
                  })
                  ?.catch((e) =>
                    setModalVisible((prev) => ({
                      ...prev,
                      loading: false,
                      error: e?.message,
                    }))
                  )
                  .finally(() => {
                    fetch();
                  });
              } else {
                (isLocation ? create(name, locationId) : create(name))
                  ?.then(() => {
                    setModalVisible(false);
                  })
                  ?.catch((e) =>
                    setModalVisible((prev) => ({
                      ...prev,
                      loading: false,
                      error: e?.message,
                    }))
                  )
                  .finally(() => {
                    fetch();
                  });
              }
            }}
          />
        ))}
      {deleteModal && <ConfirmationModal {...deleteModal} />}
    </Box>
  );
};

export default MasterTemplateTable;
